// AI 剧本生成服务
// 与后端 API 交互

import type { AxiosProgressEvent } from 'axios'
import apiClient from './api'
import type {
  ScriptGenerationTask,
  ScriptGenerationTaskDetailResponse,
  CreateScriptGenerationTaskRequest,
  ScriptGenerationTaskStatus,
  ScriptConfigPreview,
  PhaseResult,
} from '@/types/scriptGeneration'

// API base path
const BASE_URL = '/script-generation'

// ==================== 类型定义 ====================

export interface TaskListParams {
  page?: number
  page_size?: number
  status?: string
  search?: string
  version?: string
}

export interface TaskListResponse {
  tasks: ScriptGenerationTask[]
  total: number
  page: number
  page_size: number
}

export interface CreateTaskResponse {
  taskId: number
  taskName: string
  status: ScriptGenerationTaskStatus
  createdAt: string
}

export interface UploadScriptResponse {
  file_id: string
  file_name: string
  file_size: number
}

// ==================== 工具函数：snake_case 转 camelCase ====================

/**
 * 将 snake_case 转换为 camelCase
 * @param str - snake_case 格式的字符串
 * @returns camelCase 格式的字符串
 * @example
 * toCamelCase('task_id') // 'taskId'
 * toCamelCase('file_name') // 'fileName'
 */
function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * 递归转换对象的所有键为 camelCase
 * @param obj - 需要转换的对象（支持嵌套对象和数组）
 * @returns 键名为 camelCase 的新对象
 * @example
 * transformToCamelCase({ task_id: '123', user_name: 'test' })
 * // { taskId: '123', userName: 'test' }
 */
function transformToCamelCase<T>(obj: unknown): T {
  // 处理 null 和 undefined
  if (obj === null || obj === undefined) {
    return obj as T
  }

  // 处理数组（递归转换每个元素）
  if (Array.isArray(obj)) {
    return obj.map((item) => transformToCamelCase(item)) as T
  }

  // 处理普通对象（递归转换键名和值）
  if (typeof obj === 'object') {
    const result = {} as Record<string, unknown>
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[toCamelCase(key)] = transformToCamelCase((obj as Record<string, unknown>)[key])
      }
    }
    return result as T
  }

  // 基本类型直接返回
  return obj as T
}

// ==================== API 函数 ====================

// 注意：剧本生成 API 保持后端原始 snake_case 格式
// 因为 v4/v5 数据结构复杂，嵌套字段多，统一使用 snake_case 更稳定

/**
 * 获取任务列表
 */
export const getScriptGenerationTasks = async (
  params?: TaskListParams
): Promise<TaskListResponse> => {
  const response = await apiClient.get<any>(`${BASE_URL}/tasks`, { params })
  // 保持 snake_case，不进行转换
  return response.data as TaskListResponse
}

/**
 * 获取任务详情
 */
export const getScriptGenerationTaskDetail = async (
  taskId: string
): Promise<ScriptGenerationTaskDetailResponse> => {
  const response = await apiClient.get<any>(`${BASE_URL}/tasks/${taskId}`)
  const data = response.data

  // 映射 ai_call_logs 字段
  if (data.ai_call_logs && Array.isArray(data.ai_call_logs)) {
    data.ai_call_logs = data.ai_call_logs.map((log: any) => ({
      id: log.id,
      phase: log.phase || log.call_purpose || log.task_type,
      model: log.model || log.model_id,
      tokens: {
        input: log.tokens?.input ?? log.input_tokens ?? 0,
        output: log.tokens?.output ?? log.output_tokens ?? 0,
        total: log.tokens?.total ?? log.total_tokens ?? 0,
      },
      cost: log.cost ?? parseFloat(log.cost_yuan || 0),
      latency: log.latency ?? log.latency_ms ?? 0,
      status: log.status,
      error_message: log.error_message,
      request_data: log.request_data,
      response_data: log.response_data,
      retry_count: log.retry_count ?? 0,
      created_at: log.created_at,
    }))
  }

  return data as ScriptGenerationTaskDetailResponse
}

/**
 * 获取任务状态（轻量级，用于轮询）
 * 注意：返回 snake_case 格式，保持与后端一致
 */
export const getScriptGenerationTaskStatus = async (
  taskId: string
): Promise<{
  status: ScriptGenerationTaskStatus
  progress: number
  current_phase: number
  phase_name?: string
  error_message?: string
  phase_results: PhaseResult[]
}> => {
  const response = await apiClient.get<any>(`${BASE_URL}/tasks/${taskId}/status`)
  // 保持 snake_case，不进行转换
  return response.data as {
    status: ScriptGenerationTaskStatus
    progress: number
    current_phase: number
    phase_name?: string
    error_message?: string
    phase_results: PhaseResult[]
  }
}

/**
 * 创建任务
 */
export const createScriptGenerationTask = async (
  data: CreateScriptGenerationTaskRequest
): Promise<CreateTaskResponse> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks`, data)
  return transformToCamelCase<CreateTaskResponse>(response.data)
}

/**
 * 上传剧本文件
 */
export const uploadScriptFile = async (
  file: File,
  onUploadProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<UploadScriptResponse> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post<any>(`${BASE_URL}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (progressEvent.total && onUploadProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onUploadProgress(progress)
      }
    },
    signal,
  })

  return transformToCamelCase<UploadScriptResponse>(response.data)
}

/**
 * 启动任务
 */
export const startScriptGenerationTask = async (
  taskId: string
): Promise<{ taskId: string; status: string; message: string; estimatedDuration?: string }> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/${taskId}/start`)
  return transformToCamelCase<{ taskId: string; status: string; message: string; estimatedDuration?: string }>(response.data)
}

/**
 * 启动待处理任务（使用默认配置）
 * 用于处理 pending 状态但没有配置的任务
 */
export const startPendingScriptGenerationTask = async (
  taskId: string,
  version: string
): Promise<{ taskId: string; status: string; message: string }> => {
  if (version === '5.0') {
    // v5: 先配置默认参数，然后启动
    await configureV5ScriptGenerationTask(taskId, {
      narrative: {
        target_audience: '18-35岁',
        difficulty_level: 'medium',
        writing_style: 'modern',
        theme_tags: ['悬疑', '推理']
      },
      space: {
        public_space_range: [3, 5],
        private_space_range: [5, 10],
        faction_count_range: [2, 4]
      },
      npc: {
        mode: 'optional',
        count_range: [2, 3]
      },
      scale: {
        total_player_count: 10,
        total_actor_count: 5,
        group_count: 2
      },
      default_personas: {
        players: [{ template_key: 'detective_player', custom_name: '默认玩家' }],
        actors: [{ template_key: 'guide_actor', custom_name: '默认演员' }]
      }
    })
  } else {
    // v4: 配置默认参数，然后启动
    await configureV4ScriptGenerationTask(taskId, {
      input_type: 'text',
      input_text: '请根据任务描述生成剧本'
    })
  }

  // 启动任务
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/${taskId}/start`)
  return transformToCamelCase<{ taskId: string; status: string; message: string }>(response.data)
}

/**
 * 取消任务
 */
export const cancelScriptGenerationTask = async (taskId: string): Promise<void> => {
  await apiClient.post(`${BASE_URL}/tasks/${taskId}/cancel`)
}

/**
 * 暂停任务
 */
export const pauseScriptGenerationTask = async (
  taskId: string
): Promise<{ taskId: string; status: string; message: string }> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/${taskId}/pause`)
  return transformToCamelCase<{ taskId: string; status: string; message: string }>(response.data)
}

/**
 * 恢复任务
 */
export const resumeScriptGenerationTask = async (
  taskId: string
): Promise<{ taskId: string; status: string; message: string }> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/${taskId}/resume`)
  return transformToCamelCase<{ taskId: string; status: string; message: string }>(response.data)
}

/**
 * 重试任务
 */
export const retryScriptGenerationTask = async (
  taskId: string
): Promise<{ taskId: string; status: string; message: string; estimatedDuration?: string }> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/${taskId}/retry`)
  return transformToCamelCase<{ taskId: string; status: string; message: string; estimatedDuration?: string }>(response.data)
}

/**
 * 从指定阶段重试任务
 * @param taskId - 任务ID
 * @param phase - 起始阶段 (1-8)
 * @returns 任务信息
 */
export const retryScriptGenerationTaskFromPhase = async (
  taskId: string,
  phase: number
): Promise<{ taskId: string; status: string; message: string; estimatedDuration?: string }> => {
  const response = await apiClient.post<any>(
    `${BASE_URL}/tasks/${taskId}/phases/${phase}/retry`
  )
  return transformToCamelCase<{ taskId: string; status: string; message: string; estimatedDuration?: string }>(response.data)
}

/**
 * 删除任务
 */
export const deleteScriptGenerationTask = async (taskId: string): Promise<void> => {
  await apiClient.delete(`${BASE_URL}/tasks/${taskId}`)
}

/**
 * 获取生成的配置预览
 */
export const getScriptGenerationConfigPreview = async (
  taskId: string
): Promise<ScriptConfigPreview> => {
  const response = await apiClient.get<any>(`${BASE_URL}/tasks/${taskId}/preview`)
  return transformToCamelCase<ScriptConfigPreview>(response.data)
}

/**
 * 下载生成的剧本包
 */
export const downloadScriptPackage = async (taskId: string): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`${BASE_URL}/tasks/${taskId}/download`, {
    responseType: 'blob',
  })
  return response.data
}

// ==================== v5 分步创建 API ====================

/**
 * v5: 初始化任务
 */
export const initScriptGenerationTask = async (
  data: { task_name: string; task_description?: string; version: '4.0' | '5.0' }
): Promise<{ taskId: string; version: string; status: string; message: string }> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/init`, data)
  return transformToCamelCase<{ taskId: string; version: string; status: string; message: string }>(response.data)
}

/**
 * v5: 配置 v4 任务
 */
export const configureV4ScriptGenerationTask = async (
  taskId: string,
  data: {
    input_type: 'text' | 'file'
    input_text?: string
    input_file_url?: string
    input_file_name?: string
  }
): Promise<{ taskId: string; status: string; message: string }> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/${taskId}/configure/v4`, data)
  return transformToCamelCase<{ taskId: string; status: string; message: string }>(response.data)
}

/**
 * v5: 配置 v5 任务
 */
export const configureV5ScriptGenerationTask = async (
  taskId: string,
  data: {
    narrative: {
      target_audience: string
      difficulty_level: 'easy' | 'medium' | 'hard' | 'expert'
      writing_style: 'classical' | 'modern' | 'suspense' | 'comedy'
      theme_tags: string[]
    }
    space: {
      public_space_range: number[]
      private_space_range: number[]
      faction_count_range: number[]
    }
    npc: {
      mode: 'disabled' | 'optional' | 'required'
      count_range: number[]
    }
    scale: {
      total_player_count: number
      total_actor_count: number
      group_count: number
    }
    default_personas: {
      players: Array<{ template_key: string; custom_name?: string; custom_traits?: Record<string, unknown> }>
      actors: Array<{ template_key: string; custom_name?: string; custom_traits?: Record<string, unknown> }>
    }
  }
): Promise<{ taskId: string; status: string; message: string }> => {
  const response = await apiClient.post<any>(`${BASE_URL}/tasks/${taskId}/configure/v5`, data)
  return transformToCamelCase<{ taskId: string; status: string; message: string }>(response.data)
}

/**
 * v5: 获取默认角色模板
 */
export const getDefaultPersonaTemplates = async (): Promise<{
  playerTemplates: Array<{ key: string; name: string; description: string; defaultAttributes: Record<string, unknown> }>
  actorTemplates: Array<{ key: string; name: string; description: string; defaultAttributes: Record<string, unknown> }>
}> => {
  const response = await apiClient.get<any>(`${BASE_URL}/tasks/templates/personas`)
  return transformToCamelCase<{
    playerTemplates: Array<{ key: string; name: string; description: string; defaultAttributes: Record<string, unknown> }>
    actorTemplates: Array<{ key: string; name: string; description: string; defaultAttributes: Record<string, unknown> }>
  }>(response.data)
}

export default {
  getScriptGenerationTasks,
  getScriptGenerationTaskDetail,
  getScriptGenerationTaskStatus,
  createScriptGenerationTask,
  uploadScriptFile,
  cancelScriptGenerationTask,
  pauseScriptGenerationTask,
  resumeScriptGenerationTask,
  retryScriptGenerationTask,
  retryScriptGenerationTaskFromPhase,
  deleteScriptGenerationTask,
  getScriptGenerationConfigPreview,
  downloadScriptPackage,
  startPendingScriptGenerationTask,
}

// 验证数据转换的测试代码
